import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const useAgentConvoPauseMutation = ({
  dealId,
  shortId,
  onSuccess,
  onError,
}: {
  dealId: Deal['id'] | null | undefined;
  shortId: Deal['short_id'] | null | undefined;
  onSuccess?: (data: Deal, variables: { agentConvoPaused: boolean }) => void;
  onError?: (error: Error, variables: { agentConvoPaused: boolean }) => void;
}) => {
  const queryClient = useQueryClient();

  const agentConvoPauseMutation = useMutation({
    onMutate: async ({ agentConvoPaused }) => {
      if (!dealId || !shortId) {
        throw new Error('Deal ID or short ID is required');
      }

      dealStore.updateAgentConvoPaused({
        id: dealId,
        agentConvoPaused,
      });
    },
    mutationFn: async ({ agentConvoPaused }: { agentConvoPaused: boolean }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/pause-unpause-agent-convo`, {
          agent_convo_paused: agentConvoPaused,
        })
        .catch((e) => {
          console.error('Error updating agent conversation pause status', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);

      console.error(
        'Error updating agent conversation pause status',
        error,
        variables,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', shortId] });
    },
  });

  return { agentConvoPauseMutation };
};

export default useAgentConvoPauseMutation;
